/**
 * ComplaintDetail (C-2) — single complaint: metadata, lifecycle FSM actions,
 * medical (PV) assessment, recall assessment, Related Records (C-4), timeline.
 * Route: /complaints/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './Complaints.module.css';
import { RelatedRecords } from '../../components/RelatedRecords';
import {
  useComplaintDetailQuery, useComplaintTransition, useComplaintMedicalAssessment,
  useComplaintRecallAssessment, COMPLAINT_FSM, STATUS_LABEL, CATEGORY_LABEL, RECALL_LABEL,
} from './useComplaints';

function fmtDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }); }
  catch { return String(iso); }
}
function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

export function ComplaintDetail({ complaintId }: { complaintId: string }) {
  const q = useComplaintDetailQuery(complaintId);
  const transition = useComplaintTransition(complaintId);
  const medical = useComplaintMedicalAssessment(complaintId);
  const recall = useComplaintRecallAssessment(complaintId);

  const [medText, setMedText] = useState('');
  const [recallKind, setRecallKind] = useState('UNDER_EVALUATION');
  const [recallNote, setRecallNote] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading complaint…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Complaint not found. <Link to="/complaints">Back to complaints</Link></div></main>;
  const c = q.data;
  const nextStates = COMPLAINT_FSM[c.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try { await transition.mutateAsync({ toStatus: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Transition failed.'); }
  }
  async function doMedical() {
    setMsg(null);
    if (!medText.trim()) { setMsg('Enter the medical assessment outcome.'); return; }
    try { await medical.mutateAsync({ outcome: medText.trim() }); setMedText(''); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Medical assessment failed.'); }
  }
  async function doRecall() {
    setMsg(null);
    try { await recall.mutateAsync({ assessment: recallKind, justification: recallNote.trim() || undefined }); setRecallNote(''); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Recall assessment failed.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/complaints" className={styles.crumb}>Complaints</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{c.complaintNumber}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{c.complaintNumber}</span> · {c.productName}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{STATUS_LABEL[c.status] ?? c.status}</span>
            <span className={`${styles.pill} ${c.criticality === 'CRITICAL' ? styles.critCritical : c.criticality === 'MAJOR' ? styles.critMajor : styles.critMinor}`}>{c.criticality}</span>
            {c.isAdverseEvent && <span className={`${styles.pill} ${styles.aePill}`}>Adverse event</span>}
            <span className={styles.sub}>{CATEGORY_LABEL[c.category] ?? c.category}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {nextStates.length === 0 ? (
            <span className={styles.terminal}>Terminal state</span>
          ) : nextStates.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'REJECTED' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>
              → {STATUS_LABEL[to] ?? to}
            </button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Complaint detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Batch</dt><dd>{c.batchNumber ?? '—'}</dd></div>
          <div><dt>Market</dt><dd>{c.market ?? '—'}</dd></div>
          <div><dt>Complainant</dt><dd>{c.complainant ?? '—'}</dd></div>
          <div><dt>Owner</dt><dd>{c.ownerName ?? '—'}</dd></div>
          <div><dt>Received</dt><dd>{fmtDate(c.receivedDate)}</dd></div>
          <div><dt>Due</dt><dd>{fmtDate(c.dueDate)}</dd></div>
          <div><dt>Recall assessment</dt><dd>{RECALL_LABEL[c.recallAssessment] ?? c.recallAssessment}</dd></div>
          <div><dt>Closed</dt><dd>{fmtDT(c.closedAt)}</dd></div>
        </dl>
        <div className={styles.descBlock}><strong>Description</strong><p>{c.description}</p></div>
        {c.investigationSummary && <div className={styles.descBlock}><strong>Investigation</strong><p>{c.investigationSummary}</p></div>}
        {c.rootCause && <div className={styles.descBlock}><strong>Root cause</strong><p>{c.rootCause}</p></div>}
      </section>

      {/* Medical / PV assessment */}
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Medical / PV assessment</h2>
        {c.medicalAssessment ? (
          <div className={styles.assessmentDone}>
            <p>{c.medicalAssessment}</p>
            <span className={styles.sub}>Assessed {fmtDT(c.medicalAssessedAt)}</span>
          </div>
        ) : (
          <div className={styles.inlineForm}>
            <textarea rows={2} value={medText} onChange={(e) => setMedText(e.target.value)} placeholder="Medical / pharmacovigilance outcome…" className={styles.textarea} />
            <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void doMedical()} disabled={medical.isPending}>
              {medical.isPending ? 'Saving…' : 'Record assessment'}
            </button>
          </div>
        )}
      </section>

      {/* Recall assessment */}
      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Recall assessment</h2>
        <div className={styles.inlineForm}>
          <select value={recallKind} onChange={(e) => setRecallKind(e.target.value)} className={styles.select}>
            <option value="NOT_REQUIRED">Not required</option>
            <option value="UNDER_EVALUATION">Under evaluation</option>
            <option value="RECALL_INITIATED">Recall initiated</option>
          </select>
          <input type="text" value={recallNote} onChange={(e) => setRecallNote(e.target.value)} placeholder="Justification (optional)" className={styles.textInput} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void doRecall()} disabled={recall.isPending}>
            {recall.isPending ? 'Saving…' : 'Set recall status'}
          </button>
        </div>
        {c.recallJustification && <p className={styles.sub}>Current: {RECALL_LABEL[c.recallAssessment]} — {c.recallJustification}</p>}
      </section>

      {/* C-4 — Related Records (Deviation / CAPA / OOS / Risk / Change Control). */}
      <RelatedRecords entityType="COMPLAINT" entityId={c.complaintId} />

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(c.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.event_kind === 'COMPLAINT_TRANSITION' && h.from_status
                  ? `${STATUS_LABEL[h.from_status] ?? h.from_status} → ${STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}`
                  : h.event_kind.replaceAll('_', ' ').toLowerCase()}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
